import { ComponentType, createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ArchimateIcons } from './assets/archimate/ArchimateIcons';
import { BpmnIcons } from './assets/bpmn/BpmnIcons';
import { fontAwesomeIcons } from './fontAwesomeIcons';
import { ArdoqIcon, ArdoqIconCategory, IconName } from './types';
import { capitalize } from 'lodash';
import { EdgyIcons } from './assets/edgy/EdgyIcons';

const customIconLabels = new Map<string, string>([
  [IconName.VALUE_PROP, 'Value Proposition'],
  [IconName.LANGUAGE, 'Language/library'],
  [IconName.PERSON_UC, 'Person'],
]);

export const formatIconLabel = (label: string) =>
  customIconLabels.get(label) ||
  capitalize(
    label
      .replace(/^(svg_sprite_|remix_)/, '') // remove sprite prefix
      .replace(/((-o)$|-+|_+)/g, ' ') // replace - or _ with empty space
      .trim()
  );

const Icons: Record<string, ArdoqIcon> = {};

const registerFontAwesomeIcons = () => {
  Object.keys(fontAwesomeIcons).forEach(name => {
    Icons[name] = {
      id: name,
      label: formatIconLabel(name),
      category: ArdoqIconCategory.FontAwesome,
    };
  });
};

export const SOLUTION_ICON_NAMES: readonly IconName[] = [
  IconName.APPLICATION,
  IconName.APPLICATION_GROUP,
  IconName.APPLICATION_MODULE,
  IconName.ARCHITECTURE_STANDARD,
  IconName.BUSINESS_CAPABILITY,
  IconName.BUSINESS_HEALTH_CHECK,
  IconName.CAPABILITY_DELTA,
  IconName.CATEGORY,
  IconName.CHANNEL,
  IconName.COMPLIANCE_ASSESSMENT,
  IconName.CONTRACT,
  IconName.CONTROL,
  IconName.CUSTOMER_SEGMENT,
  IconName.DATA_ENTITY,
  IconName.DATA_STORE,
  IconName.DEBT_ITEM,
  IconName.DECISION_RECORD,
  IconName.DESIGN_OBJECT,
  IconName.DIGITAL_CHANNEL,
  IconName.DIGITAL_PRODUCT,
  IconName.EXCEPTION_REQUEST,
  IconName.FUTURE_STATE,
  IconName.INFORMATION_ARTIFACT,
  IconName.INITIATIVE,
  IconName.INTERFACE,
  IconName.KEY_RESULT,
  IconName.LANGUAGE,
  IconName.LOCATION,
  IconName.MARKET,
  IconName.MESSAGE_FORMAT,
  IconName.METRIC,
  IconName.NETWORK_ZONE,
  IconName.NODE,
  IconName.OBJECTIVE,
  IconName.ORGANIZATION,
  IconName.ORGANIZATIONAL_UNIT,
  IconName.PATTERN,
  IconName.PERSON_UC,
  IconName.PERSONA,
  IconName.PROCESS,
  IconName.PROCESS_ACTIVITY,
  IconName.PRODUCT,
  IconName.REQUIREMENT,
  IconName.RISK,
  IconName.ROLE,
  IconName.SERVER,
  IconName.STRATEGY,
  IconName.SUBJECT_AREA,
  IconName.TECHNICAL_CAPABILITY,
  IconName.TECHNOLOGY_HEALTH_CHECK,
  IconName.TECHNOLOGY_PRODUCT,
  IconName.TECHNOLOGY_SERVICE,
  IconName.VALUE_PROP,
  IconName.VALUE_STREAM,
  IconName.VALUE_STREAM_STAGE,
  IconName.VULNERABILITY,
];

const registerSolutionIcons = () => {
  SOLUTION_ICON_NAMES.forEach(iconName => {
    Icons[iconName] = {
      id: iconName,
      label: formatIconLabel(iconName),
      category: ArdoqIconCategory.Solution,
      isSVG: true,
    };
  });
};

const registerIconsFromTemplate = (
  template: ComponentType<unknown>,
  category: ArdoqIconCategory
) => {
  new DOMParser()
    .parseFromString(renderToStaticMarkup(createElement(template)), 'text/html')
    .body.childNodes.forEach((element: any) => {
      if (!element.id || !element.dataset.label) {
        return;
      }

      Icons[element.id] = {
        id: element.id,
        label: element.dataset.label,
        category,
        isSVG: true,
      };
    });
};

export const initializeIconDefinitions = () => {
  registerFontAwesomeIcons();
  registerIconsFromTemplate(ArchimateIcons, ArdoqIconCategory.ArchiMate);
  registerIconsFromTemplate(BpmnIcons, ArdoqIconCategory.BPMN);
  registerIconsFromTemplate(EdgyIcons, ArdoqIconCategory.EDGY);
  registerSolutionIcons();
};

export const getIcons = () => Icons;
